/**
 * @typedef {{ value: string, label: string, mainId?: string, formLabel?: string }} StructFilterOption
 * @typedef {{ mainId: string, mainLabel: string, mainCode: string, subs: StructFilterOption[] }} StructSubGroup
 * @typedef {{ mains: StructFilterOption[], subGroups: StructSubGroup[] }} StructFilterModel
 */

const EMPTY_MODEL = { mains: [], subGroups: [] };

function normalizeKeyword(keyword) {
  return String(keyword ?? '').trim().toLowerCase();
}

function matchesKeyword(text, keyword) {
  if (!keyword) {
    return true;
  }

  return String(text ?? '').toLowerCase().includes(keyword);
}

function labelMatches(label, keyword) {
  return matchesKeyword(label, keyword);
}

export function formatMainLabel(main) {
  return `${main.code} - ${main.name}`;
}

export function formatSubLabel(sub) {
  return `${sub.code} - ${sub.name}`;
}

export function fromTree(treeRows = []) {
  if (!Array.isArray(treeRows) || treeRows.length === 0) {
    return EMPTY_MODEL;
  }

  const mains = treeRows.map((main) => ({
    value: main.id,
    label: formatMainLabel(main),
  }));

  const subGroups = treeRows
    .map((main) => {
      const subs = (main.subList ?? []).map((sub) => ({
        value: sub.id,
        label: formatSubLabel(sub),
        mainId: main.id,
        formLabel: `${main.code} / ${sub.code} - ${sub.name}`,
      }));

      if (subs.length === 0) {
        return null;
      }

      return {
        mainId: main.id,
        mainLabel: formatMainLabel(main),
        mainCode: main.code,
        subs,
      };
    })
    .filter(Boolean);

  return { mains, subGroups };
}

export function fromSubSearch(subRows = []) {
  if (!Array.isArray(subRows) || subRows.length === 0) {
    return EMPTY_MODEL;
  }

  const groupMap = new Map();

  subRows.forEach((sub) => {
    const main = sub.mtrStructMain;
    const mainId = main?.id ?? sub.mtrStructMainId ?? '';

    if (!mainId) {
      return;
    }

    if (!groupMap.has(mainId)) {
      groupMap.set(mainId, {
        mainId,
        mainLabel: main ? formatMainLabel(main) : mainId,
        mainCode: main?.code ?? '',
        subs: [],
      });
    }

    groupMap.get(mainId).subs.push({
      value: sub.id,
      label: formatSubLabel(sub),
      mainId,
      formLabel: main
        ? `${main.code} / ${sub.code} - ${sub.name}`
        : formatSubLabel(sub),
    });
  });

  const subGroups = Array.from(groupMap.values());
  const mains = subGroups.map((group) => ({
    value: group.mainId,
    label: group.mainLabel,
  }));

  return { mains, subGroups };
}

export function filterMainOptions(model, keyword = '') {
  const normalizedKeyword = normalizeKeyword(keyword);

  if (!normalizedKeyword) {
    return model.mains;
  }

  return model.mains.filter((option) => labelMatches(option.label, normalizedKeyword));
}

export function filterSubGroups(model, keyword = '') {
  const normalizedKeyword = normalizeKeyword(keyword);

  if (!normalizedKeyword) {
    return model.subGroups;
  }

  return model.subGroups
    .map((group) => {
      const mainMatchesKeyword = labelMatches(group.mainLabel, normalizedKeyword);
      const visibleSubs = mainMatchesKeyword
        ? group.subs
        : group.subs.filter((sub) => labelMatches(sub.label, normalizedKeyword));

      if (visibleSubs.length === 0) {
        return null;
      }

      return {
        ...group,
        subs: visibleSubs,
      };
    })
    .filter(Boolean);
}

export function flattenSubOptions(model) {
  return model.subGroups.flatMap((group) => group.subs);
}

export function buildFormSubSelectOptions(model) {
  return flattenSubOptions(model).map((sub) => ({
    value: sub.value,
    label: sub.formLabel ?? sub.label,
    mainId: sub.mainId,
  }));
}

export function resolveMainIdBySubId(model, subId = '') {
  if (!subId) {
    return '';
  }

  const matchedGroup = model.subGroups.find((group) => (
    group.subs.some((sub) => sub.value === subId)
  ));

  return matchedGroup?.mainId ?? '';
}
